/**
 * DashboardDirectionPreview — a public, no-auth comparison page that renders
 * three candidate dashboard directions side-by-side (switchable) so we can
 * pick a single "central" dashboard. Optimized for a NEW real-estate pro whose
 * #1 nudge is "Start / set up a new project". Uses baked-in sample data so the
 * shareable link works instantly in any environment.
 *
 * Route: /dashboard-direction-preview
 */
import React, { useState } from 'react';
import {
  Plus, ArrowRight, Wallet, Activity, Shield, Target, Building2, ChevronRight,
  CheckCircle2, Circle, Clock, AlertTriangle, FileText, Users, CalendarDays,
  Sparkles, Rocket, ListChecks, LayoutDashboard, DollarSign,
  UploadCloud, Table2, ScanLine, Link2, RefreshCw, ArrowRightLeft, Loader2,
} from 'lucide-react';

const NAVY = '#0b3b6f';
const GREEN = '#5c9e33';

// ---------- shared sample data (a "just getting started" portfolio) ----------
const SAMPLE = {
  ownerName: 'Jordan',
  projects: [
    { id: 1, name: 'Maple Ridge Townhomes', addr: 'Cedar Rapids, IA', drawn: 41, loan: 343000, avail: 202000, daily: 38.42, score: 88, step: 'Request next draw', status: 'active', gaps: 0 },
    { id: 2, name: 'Oakwood Infill', addr: 'Iowa City, IA', drawn: 12, loan: 210000, avail: 184000, daily: 11.10, score: 72, step: 'Collect 2 contractor W-9s', status: 'active', gaps: 2 },
    { id: 3, name: 'Ellis Row (draft)', addr: '204 Ellis Blvd', drawn: 0, loan: 0, avail: 0, daily: 0, score: 35, step: 'Finish loan setup', status: 'setup', gaps: 0 },
  ],
};

const money = (v) => '$' + (v >= 1000 ? (v / 1000).toFixed(0) + 'K' : Math.round(v));

// ============================================================================
// VARIANT A — "Next Best Action" hero
// ============================================================================
const VariantA = () => (
  <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-5">
    {/* Hero: the ONE next step */}
    <div className="rounded-3xl p-6 sm:p-8 text-white relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${NAVY}, #12508f)` }}>
      <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full opacity-20" style={{ background: GREEN }} />
      <div className="relative">
        <p className="text-[11px] font-bold uppercase tracking-widest" style={{ color: '#a8d977' }}>Your next step</p>
        <h2 className="text-2xl sm:text-3xl font-extrabold mt-1 leading-tight">Set up your next project</h2>
        <p className="text-blue-100 mt-2 max-w-lg text-sm sm:text-base">
          Add the property + loan details and DrawReady builds your budget, draw schedule, and bank forms automatically. Takes about 3 minutes.
        </p>
        <div className="flex flex-wrap gap-3 mt-5">
          <button className="inline-flex items-center gap-2 bg-white text-[15px] font-bold px-6 py-3 rounded-xl hover:shadow-lg transition" style={{ color: NAVY }}>
            <Plus className="w-5 h-5" /> Start a project
          </button>
          <button className="inline-flex items-center gap-2 border border-white/40 text-white text-sm font-semibold px-5 py-3 rounded-xl hover:bg-white/10 transition">
            Watch 90-sec tour
          </button>
        </div>
      </div>
    </div>

    {/* Needs attention — collapsed to a single strip */}
    <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-center justify-between">
      <div className="flex items-center gap-2 text-sm text-amber-900">
        <AlertTriangle className="w-4 h-4 text-amber-600" />
        <span><b>3 things</b> need attention — 2 contractor W-9s, 1 draw ready, 1 loan to finish.</span>
      </div>
      <button className="text-xs font-semibold text-amber-800 inline-flex items-center gap-1 hover:underline">Review <ChevronRight className="w-3.5 h-3.5" /></button>
    </div>

    {/* Quiet KPIs */}
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {[
        { icon: Wallet, label: 'Available to draw', val: '$386K' },
        { icon: Activity, label: 'Daily interest', val: '$49.52' },
        { icon: Target, label: 'Bank-ready score', val: '80' },
        { icon: Shield, label: 'Compliance', val: '1 / 3' },
      ].map((k) => (
        <div key={k.label} className="rounded-2xl border border-slate-200 bg-white p-4">
          <k.icon className="w-4 h-4 text-slate-400" />
          <p className="text-xl font-bold text-slate-900 mt-2">{k.val}</p>
          <p className="text-[11px] text-slate-500">{k.label}</p>
        </div>
      ))}
    </div>

    {/* Loans, demoted + quiet */}
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 text-sm font-bold text-slate-700 flex items-center gap-2">
        <Building2 className="w-4 h-4 text-slate-400" /> Your projects
      </div>
      {SAMPLE.projects.map((p) => (
        <div key={p.id} className="px-4 py-3 flex items-center gap-3 border-b border-slate-50 last:border-0 hover:bg-slate-50">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-800 truncate">{p.name}</p>
            <p className="text-[11px] text-slate-400">{p.addr}</p>
          </div>
          <span className="text-[11px] text-slate-500 hidden sm:block">{p.drawn}% drawn</span>
          <ChevronRight className="w-4 h-4 text-slate-300" />
        </div>
      ))}
    </div>
  </div>
);

// ============================================================================
// VARIANT B — Guided setup tracker
// ============================================================================
const STEPS = [
  { label: 'Create project', done: true },
  { label: 'Add loan details', done: true },
  { label: 'Invite contractors', done: false, active: true },
  { label: 'Build draw schedule', done: false },
  { label: 'Request first draw', done: false },
];
const VariantB = () => {
  const doneCount = STEPS.filter((s) => s.done).length;
  const pct = Math.round((doneCount / STEPS.length) * 100);
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-5">
      {/* Get-started header with progress */}
      <div className="rounded-3xl border border-slate-200 bg-white p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-widest" style={{ color: GREEN }}>Getting started</p>
            <h2 className="text-2xl font-extrabold text-slate-900 mt-1">You&apos;re {pct}% set up, {SAMPLE.ownerName}</h2>
            <p className="text-slate-500 text-sm mt-1">Finish these steps to send your first bank-ready draw.</p>
          </div>
          <div className="relative w-20 h-20 flex items-center justify-center">
            <svg className="w-20 h-20 -rotate-90"><circle cx="40" cy="40" r="34" fill="none" stroke="#e2e8f0" strokeWidth="8" /><circle cx="40" cy="40" r="34" fill="none" stroke={GREEN} strokeWidth="8" strokeDasharray={`${2 * Math.PI * 34}`} strokeDashoffset={`${2 * Math.PI * 34 * (1 - pct / 100)}`} strokeLinecap="round" /></svg>
            <span className="absolute text-lg font-bold text-slate-900">{pct}%</span>
          </div>
        </div>
        {/* Stepper */}
        <div className="mt-5 space-y-2">
          {STEPS.map((s, i) => (
            <div key={i} className={`flex items-center gap-3 rounded-xl px-3 py-2.5 ${s.active ? 'bg-blue-50 border border-blue-200' : ''}`}>
              {s.done ? <CheckCircle2 className="w-5 h-5" style={{ color: GREEN }} /> : s.active ? <Clock className="w-5 h-5 text-blue-600" /> : <Circle className="w-5 h-5 text-slate-300" />}
              <span className={`text-sm flex-1 ${s.done ? 'text-slate-400 line-through' : s.active ? 'font-bold text-slate-900' : 'text-slate-500'}`}>{s.label}</span>
              {s.active && <button className="text-xs font-bold text-white px-3 py-1.5 rounded-lg" style={{ background: NAVY }}>Continue</button>}
            </div>
          ))}
        </div>
      </div>

      {/* Primary CTA: start ANOTHER project */}
      <button className="w-full rounded-2xl border-2 border-dashed border-slate-300 py-5 flex items-center justify-center gap-2 text-slate-600 hover:border-blue-400 hover:text-blue-700 hover:bg-blue-50 transition font-semibold">
        <Plus className="w-5 h-5" /> Start a new project
      </button>

      {/* Per-project next-step cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {SAMPLE.projects.map((p) => (
          <div key={p.id} className="rounded-2xl border border-slate-200 bg-white p-4">
            <p className="text-sm font-bold text-slate-900 truncate">{p.name}</p>
            <p className="text-[11px] text-slate-400 mb-3">{p.addr}</p>
            <div className="rounded-lg bg-slate-50 px-3 py-2 text-xs">
              <span className="text-slate-400 block text-[10px] uppercase font-bold tracking-wide">Next step</span>
              <span className="text-slate-800 font-medium">{p.step}</span>
            </div>
            <button className="mt-3 w-full text-xs font-semibold text-blue-700 border border-blue-200 rounded-lg py-2 hover:bg-blue-50">Continue →</button>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// VARIANT C — Focused feed (declutter + one primary CTA)
// ============================================================================
const VariantC = () => (
  <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
    <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
      <div>
        <h2 className="text-2xl font-extrabold text-slate-900">Dashboard</h2>
        <p className="text-sm text-slate-500">Everything that needs you, in one list.</p>
      </div>
      <button className="inline-flex items-center gap-2 text-white font-bold text-sm px-5 py-2.5 rounded-xl" style={{ background: NAVY }}>
        <Plus className="w-4 h-4" /> New Project
      </button>
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Left: single Attention feed */}
      <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 text-sm font-bold text-slate-800">
          <Sparkles className="w-4 h-4" style={{ color: GREEN }} /> Attention feed
        </div>
        {[
          { icon: Rocket, tone: 'green', t: 'Set up your next project', s: 'Your fastest path to a bank-ready draw.', cta: 'Start' },
          { icon: DollarSign, tone: 'blue', t: 'Maple Ridge is 41% drawn', s: 'Ready to request your next draw?', cta: 'Request draw' },
          { icon: Users, tone: 'amber', t: '2 contractor W-9s missing', s: 'Oakwood Infill — collect before the next draw.', cta: 'Collect' },
          { icon: FileText, tone: 'slate', t: 'Finish Ellis Row loan setup', s: 'Draft project — add loan terms to activate.', cta: 'Finish' },
        ].map((row, i) => {
          const tones = { green: 'bg-emerald-50 text-emerald-700', blue: 'bg-blue-50 text-blue-700', amber: 'bg-amber-50 text-amber-700', slate: 'bg-slate-100 text-slate-600' };
          return (
            <div key={i} className="px-4 py-3 flex items-center gap-3 border-b border-slate-50 last:border-0 hover:bg-slate-50">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tones[row.tone]}`}><row.icon className="w-4 h-4" /></div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800">{row.t}</p>
                <p className="text-[11px] text-slate-400">{row.s}</p>
              </div>
              <button className="text-xs font-bold px-3 py-1.5 rounded-lg text-white flex-shrink-0" style={{ background: i === 0 ? GREEN : NAVY }}>{row.cta}</button>
            </div>
          );
        })}
      </div>

      {/* Right: compact portfolio */}
      <div className="space-y-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <p className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2"><Building2 className="w-4 h-4 text-slate-400" /> Projects</p>
          {SAMPLE.projects.map((p) => (
            <div key={p.id} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
              <div className="min-w-0">
                <p className="text-xs font-semibold text-slate-800 truncate">{p.name}</p>
                <div className="h-1.5 w-24 rounded-full bg-slate-100 mt-1"><div className="h-1.5 rounded-full" style={{ width: `${p.drawn}%`, background: GREEN }} /></div>
              </div>
              <span className="text-[11px] text-slate-400">{p.drawn}%</span>
            </div>
          ))}
        </div>
        <div className="rounded-2xl p-4 text-white" style={{ background: NAVY }}>
          <p className="text-[11px] uppercase tracking-wide font-bold text-blue-200">Portfolio</p>
          <p className="text-2xl font-bold mt-1">$386K <span className="text-sm font-normal text-blue-200">available</span></p>
          <p className="text-xs text-blue-200 mt-1">3 projects · $49.52/day interest</p>
        </div>
      </div>
    </div>
  </div>
);

// ============================================================================
// VARIANT D — Migrate an in-progress project
// ============================================================================
const IMPORT_METHODS = [
  { icon: Table2, t: 'Upload a spreadsheet', s: 'Contractors, budget lines, or payments-to-date (CSV / Excel). We map the columns for you.', tag: 'Fastest', tone: 'blue' },
  { icon: ScanLine, t: 'Drop in your invoices & receipts', s: 'Bulk-upload PDFs/photos — AI reads vendor, amount, category & date and files them to the right phase.', tag: 'AI', tone: 'green' },
  { icon: Link2, t: 'Connect QuickBooks', s: 'Pull vendors, bills & payments straight from your books.', tag: 'Soon', tone: 'slate' },
  { icon: Plus, t: 'Enter it manually', s: 'Add contractors and paid amounts by hand — good for a quick start.', tag: null, tone: 'slate' },
];
const MIGRATION_STEPS = [
  { label: 'Project + loan added', sub: 'Maple Ridge · $343K · draw #4', done: true },
  { label: 'Contractors imported', sub: '12 of ~15 from spreadsheet', done: true },
  { label: 'Reading invoices', sub: 'AI extracting 8 receipts…', active: true },
  { label: 'Payments-to-date entered', sub: '$141K matched to 3 categories', done: false },
  { label: 'Reconciled to current', sub: 'Confirm you are 41% drawn', done: false },
];
const VariantD = () => (
  <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-5">
    {/* Migration hero */}
    <div className="rounded-3xl p-6 sm:p-8 text-white relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${NAVY}, #12508f)` }}>
      <div className="absolute -right-10 -bottom-10 w-48 h-48 rounded-full opacity-20" style={{ background: GREEN }} />
      <div className="relative">
        <p className="text-[11px] font-bold uppercase tracking-widest" style={{ color: '#a8d977' }}>Already have a project underway?</p>
        <h2 className="text-2xl sm:text-3xl font-extrabold mt-1 leading-tight">Bring your in-progress project into DrawReady</h2>
        <p className="text-blue-100 mt-2 max-w-xl text-sm sm:text-base">
          Import your contractors, what&apos;s already been paid, and your invoices — then set where you are today. You&apos;ll be caught up to your current draw in minutes, not days.
        </p>
        <div className="flex flex-wrap gap-3 mt-5">
          <button className="inline-flex items-center gap-2 bg-white text-[15px] font-bold px-6 py-3 rounded-xl hover:shadow-lg transition" style={{ color: NAVY }}>
            <ArrowRightLeft className="w-5 h-5" /> Start migration
          </button>
          <button className="inline-flex items-center gap-2 border border-white/40 text-white text-sm font-semibold px-5 py-3 rounded-xl hover:bg-white/10 transition">
            Book a 15-min migration call
          </button>
        </div>
      </div>
    </div>

    {/* Import methods */}
    <div>
      <p className="text-sm font-bold text-slate-700 mb-2">1 · Bring your data in — pick any (mix & match)</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {IMPORT_METHODS.map((m) => {
          const tones = { blue: 'bg-blue-50 text-blue-700', green: 'bg-emerald-50 text-emerald-700', slate: 'bg-slate-100 text-slate-600' };
          const tagt = { Fastest: 'bg-blue-600', AI: '', Soon: 'bg-slate-400', null: '' };
          return (
            <div key={m.t} className="rounded-2xl border border-slate-200 bg-white p-4 hover:border-blue-300 hover:shadow-sm transition cursor-pointer">
              <div className="flex items-start justify-between">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${tones[m.tone]}`}><m.icon className="w-5 h-5" /></div>
                {m.tag && <span className="text-[10px] font-bold uppercase tracking-wide text-white px-2 py-0.5 rounded-full" style={{ background: m.tag === 'AI' ? GREEN : (m.tag === 'Fastest' ? '#2563eb' : '#94a3b8') }}>{m.tag}</span>}
              </div>
              <p className="text-sm font-bold text-slate-900 mt-3">{m.t}</p>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">{m.s}</p>
            </div>
          );
        })}
      </div>
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      {/* Migration progress */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <p className="text-sm font-bold text-slate-700 mb-3">2 · Migration progress</p>
        <div className="space-y-1">
          {MIGRATION_STEPS.map((s, i) => (
            <div key={i} className={`flex items-start gap-3 rounded-xl px-3 py-2.5 ${s.active ? 'bg-emerald-50 border border-emerald-200' : ''}`}>
              {s.done ? <CheckCircle2 className="w-5 h-5 mt-0.5" style={{ color: GREEN }} /> : s.active ? <Loader2 className="w-5 h-5 mt-0.5 text-emerald-600 animate-spin" /> : <Circle className="w-5 h-5 mt-0.5 text-slate-300" />}
              <div className="flex-1">
                <p className={`text-sm ${s.done ? 'text-slate-500' : s.active ? 'font-bold text-slate-900' : 'text-slate-600'}`}>{s.label}</p>
                <p className="text-[11px] text-slate-400">{s.sub}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Catch up to current / reconcile */}
      <div className="space-y-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <p className="text-sm font-bold text-slate-700 mb-1 flex items-center gap-2"><RefreshCw className="w-4 h-4 text-slate-400" /> 3 · Catch up to today</p>
          <p className="text-xs text-slate-500 mb-3">Tell us where the project stands now so DrawReady starts accurate.</p>
          <div className="space-y-2.5">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">Current draw #</span>
              <span className="font-semibold text-slate-900 bg-slate-100 rounded-lg px-3 py-1">#4</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">Already drawn to date</span>
              <span className="font-semibold text-slate-900 bg-slate-100 rounded-lg px-3 py-1">$141,000</span>
            </div>
            <div>
              <div className="flex items-center justify-between text-xs text-slate-500 mb-1"><span>Progress</span><span>41% of $343K</span></div>
              <div className="h-2 rounded-full bg-slate-100"><div className="h-2 rounded-full" style={{ width: '41%', background: GREEN }} /></div>
            </div>
          </div>
        </div>
        {/* Reconciliation confidence */}
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 flex gap-3">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-emerald-900">
            <b>Looks reconciled.</b> Imported $141K across Framing, Foundation & Plumbing — matches your 41% drawn. 2 receipts need a category (we&apos;ll flag them for a quick tap).
          </div>
        </div>
      </div>
    </div>
  </div>
);

// ============================================================================
// VARIANT E — PROPOSED central dashboard (your composite:
//   Active/Completed toggle + A "Your next step" hero + B getting-started
//   tracker + C portfolio highlight)
// ============================================================================
const VariantE = () => {
  const [view, setView] = useState('active');
  const completed = [
    { id: 9, name: 'Cedar Court Duplex', addr: 'Marion, IA', drawn: 100, loan: 268000 },
  ];
  const activeProjects = SAMPLE.projects;
  const list = view === 'active' ? activeProjects : completed;
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-5">
      {/* Active / Completed toggle */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="inline-flex bg-white border border-slate-200 rounded-xl p-1" data-testid="active-completed-toggle">
          {[['active', `Active (${activeProjects.length})`], ['completed', `Completed (${completed.length})`]].map(([k, lbl]) => (
            <button key={k} onClick={() => setView(k)} className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${view === k ? 'text-white shadow' : 'text-slate-500 hover:text-slate-800'}`} style={view === k ? { background: NAVY } : {}}>{lbl}</button>
          ))}
        </div>
        <button className="inline-flex items-center gap-2 text-white font-bold text-sm px-5 py-2.5 rounded-xl" style={{ background: GREEN }}>
          <Plus className="w-4 h-4" /> Start a project
        </button>
      </div>

      {/* A — Your next step hero */}
      <div className="rounded-3xl p-6 text-white relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${NAVY}, #12508f)` }}>
        <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full opacity-20" style={{ background: GREEN }} />
        <div className="relative flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-widest" style={{ color: '#a8d977' }}>Your next step</p>
            <h2 className="text-2xl font-extrabold mt-1">Request Maple Ridge draw #5</h2>
            <p className="text-blue-100 mt-1 text-sm max-w-md">You&apos;re 41% drawn with $202K available. 3 phases are complete and ready to submit to the bank.</p>
          </div>
          <button className="inline-flex items-center gap-2 bg-white text-sm font-bold px-5 py-3 rounded-xl" style={{ color: NAVY }}>Request draw <ArrowRight className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* B — Getting started tracker (for the setup project) */}
        <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-bold text-slate-800 flex items-center gap-2"><ListChecks className="w-4 h-4" style={{ color: GREEN }} /> Finish setting up Ellis Row</p>
            <span className="text-xs font-semibold text-slate-500">40% done</span>
          </div>
          <div className="space-y-1.5">
            {[['Create project', true], ['Add loan details', true], ['Invite contractors', false, true], ['Build draw schedule', false], ['Request first draw', false]].map(([lbl, done, active], i) => (
              <div key={i} className={`flex items-center gap-3 rounded-lg px-3 py-2 ${active ? 'bg-blue-50 border border-blue-200' : ''}`}>
                {done ? <CheckCircle2 className="w-4 h-4" style={{ color: GREEN }} /> : active ? <Clock className="w-4 h-4 text-blue-600" /> : <Circle className="w-4 h-4 text-slate-300" />}
                <span className={`text-sm flex-1 ${done ? 'text-slate-400 line-through' : active ? 'font-bold text-slate-900' : 'text-slate-500'}`}>{lbl}</span>
                {active && <button className="text-xs font-bold text-white px-3 py-1 rounded-lg" style={{ background: NAVY }}>Continue</button>}
              </div>
            ))}
          </div>
          {/* projects list under tracker */}
          <div className="mt-4 border-t border-slate-100 pt-3 space-y-1">
            {list.map((p) => (
              <div key={p.id} className="flex items-center gap-3 py-2 hover:bg-slate-50 rounded-lg px-2">
                <Building2 className="w-4 h-4 text-slate-300" />
                <div className="flex-1 min-w-0"><p className="text-sm font-semibold text-slate-800 truncate">{p.name}</p><p className="text-[11px] text-slate-400">{p.addr}</p></div>
                <div className="h-1.5 w-20 rounded-full bg-slate-100"><div className="h-1.5 rounded-full" style={{ width: `${p.drawn}%`, background: GREEN }} /></div>
                <span className="text-[11px] text-slate-400 w-8 text-right">{p.drawn}%</span>
              </div>
            ))}
            {list.length === 0 && <p className="text-xs text-slate-400 py-4 text-center">No {view} projects.</p>}
          </div>
        </div>

        {/* C — Portfolio highlight */}
        <div className="space-y-4">
          <div className="rounded-2xl p-5 text-white" style={{ background: NAVY }}>
            <p className="text-[11px] uppercase tracking-wide font-bold text-blue-200">Portfolio</p>
            <p className="text-3xl font-extrabold mt-1">$386K</p>
            <p className="text-xs text-blue-200">available to draw</p>
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-blue-200">Total loans</span><span className="font-semibold">$553K</span></div>
              <div className="flex justify-between"><span className="text-blue-200">Daily interest</span><span className="font-semibold">$49.52</span></div>
              <div className="flex justify-between"><span className="text-blue-200">Active projects</span><span className="font-semibold">{activeProjects.length}</span></div>
            </div>
          </div>
          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900 flex gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" /> 2 contractor W-9s missing on Oakwood.
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// VARIANT F — "Start a project" decision flow
// ============================================================================
const FlowNode = ({ icon: Icon, title, sub, tone = 'slate' }) => {
  const tones = { navy: { bg: NAVY, c: '#fff' }, green: { bg: GREEN, c: '#fff' }, slate: { bg: '#fff', c: '#0f172a' } };
  const st = tones[tone];
  return (
    <div className="rounded-2xl border border-slate-200 p-4 shadow-sm" style={{ background: st.bg }}>
      <div className="flex items-center gap-2">
        <Icon className="w-5 h-5" style={{ color: tone === 'slate' ? GREEN : '#fff' }} />
        <p className="text-sm font-bold" style={{ color: st.c }}>{title}</p>
      </div>
      {sub && <p className="text-xs mt-1" style={{ color: tone === 'slate' ? '#64748b' : 'rgba(255,255,255,0.8)' }}>{sub}</p>}
    </div>
  );
};
const VariantF = () => (
  <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-4">
    <div className="text-center">
      <p className="text-[11px] font-bold uppercase tracking-widest" style={{ color: GREEN }}>When a user clicks &ldquo;Start a project&rdquo;</p>
      <h2 className="text-2xl font-extrabold text-slate-900 mt-1">The decision flow</h2>
    </div>

    <FlowNode icon={Plus} title="Q1 · Has this project already started?" sub="First fork — set the right on-ramp." tone="navy" />
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div className="space-y-2">
        <div className="text-center text-xs font-bold text-emerald-700">YES — already underway</div>
        <FlowNode icon={ArrowRightLeft} title="→ Migration screen (tab D)" sub="Import contractors, payments & invoices, then catch up to current draw." tone="green" />
      </div>
      <div className="space-y-2">
        <div className="text-center text-xs font-bold text-slate-500">NO — brand new</div>
        <FlowNode icon={DollarSign} title="Q2 · Already funded?" sub="+ Have contractors been selected?" tone="slate" />
      </div>
    </div>

    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <p className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2"><Building2 className="w-4 h-4 text-slate-400" /> If NOT yet funded / no contractors → Project Hub</p>
      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        {[
          [FileText, 'Send bid requests'],
          [Users, 'Compare bids'],
          [CheckCircle2, 'Award'],
          [Table2, 'Import to bid sheet'],
          [Building2, 'Send to bank'],
          [DollarSign, 'Mark funded'],
          [Rocket, 'Convert to project'],
        ].map(([Ic, lbl], i, arr) => (
          <React.Fragment key={lbl}>
            <div className="flex flex-col items-center gap-1 min-w-[84px]">
              <div className="w-9 h-9 rounded-lg bg-white border border-slate-200 flex items-center justify-center"><Ic className="w-4 h-4 text-slate-600" /></div>
              <span className="text-[10px] text-slate-600 text-center leading-tight">{lbl}</span>
            </div>
            {i < arr.length - 1 && <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />}
          </React.Fragment>
        ))}
      </div>
    </div>

    <div className="text-center"><ChevronRight className="w-5 h-5 text-slate-300 mx-auto rotate-90" /></div>
    <FlowNode icon={Rocket} title="If funded + contractors set → jump straight to project setup" sub="Skips Project Hub; lands in the guided setup tracker to finish loan + schedule." tone="navy" />
  </div>
);

// ============================================================================
// Shell + switcher
// ============================================================================
const TABS = [
  { key: 'E', label: '★ Proposed', icon: Sparkles, blurb: 'PROPOSED central dashboard — your mix: Active/Completed toggle + "Your next step" hero (A) + getting-started tracker (B) + portfolio highlight (C).' },
  { key: 'F', label: 'Start Flow', icon: ArrowRightLeft, blurb: 'What happens when a user clicks "Start a project": already-started → migration; new → funded?/contractors? → Project Hub or straight to setup.' },
  { key: 'A', label: 'Next Best Action', icon: Target, blurb: 'Building block: one hero card tells the user exactly what to do next.' },
  { key: 'B', label: 'Guided Setup', icon: ListChecks, blurb: 'Building block: a step tracker walks a new user from create → loan → contractors → draw.' },
  { key: 'C', label: 'Focused Feed', icon: LayoutDashboard, blurb: 'Building block: one "attention feed" of prioritized actions + a single primary button.' },
  { key: 'D', label: 'Migrate a Project', icon: ArrowRightLeft, blurb: 'For a user with a live project: import contractors, payments & invoices, then catch up to their current draw.' },
];

export default function DashboardDirectionPreview() {
  const [tab, setTab] = useState('E');
  const active = TABS.find((t) => t.key === tab);
  return (
    <div className="min-h-screen bg-slate-100">
      {/* Top bar */}
      <div className="sticky top-0 z-20 bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="flex items-end gap-[2px]"><div className="w-2 h-6 rounded-sm" style={{ background: GREEN }} /><div className="w-2 h-6 rounded-sm -ml-[2px]" style={{ background: GREEN }} /></div>
              <span className="font-extrabold tracking-tight" style={{ color: NAVY }}>DrawReady</span>
              <span className="text-xs text-slate-400 hidden sm:inline">· Dashboard direction preview</span>
            </div>
            <div className="inline-flex bg-slate-100 rounded-xl p-1" data-testid="direction-switcher">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  data-testid={`direction-tab-${t.key}`}
                  className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold inline-flex items-center gap-1.5 transition ${tab === t.key ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-800'}`}
                >
                  <t.icon className="w-3.5 h-3.5" /> <span className="hidden sm:inline">{t.key} · </span>{t.label}
                </button>
              ))}
            </div>
          </div>
          <p className="text-[11px] text-slate-500 mt-2">{active.blurb} <span className="text-slate-400">· Sample data · buttons are illustrative.</span></p>
        </div>
      </div>

      {/* Render selected variant */}
      <div data-testid={`variant-${tab}`}>
        {tab === 'E' && <VariantE />}
        {tab === 'F' && <VariantF />}
        {tab === 'A' && <VariantA />}
        {tab === 'B' && <VariantB />}
        {tab === 'C' && <VariantC />}
        {tab === 'D' && <VariantD />}
      </div>

      <div className="text-center text-[11px] text-slate-400 pb-10">
        Tell me which direction (or mix) you like and I&apos;ll build it as the single central dashboard on web + mobile.
      </div>
    </div>
  );
}
